import React, { useState, useEffect, useRef } from 'react';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import axios from 'axios';
import axiosPost from '../../../helpers/axios-helper';
import config from '../../../constants/config';

/**
 * Sponsored banners: the paid placements on the home page.
 *
 * Dates are held as YYYY-MM-DD strings because that is what <input type="date">
 * speaks; the API stores unix timestamps, with 0 meaning "no limit".
 */

const toInput = (unix) => {
  const n = Number(unix || 0);
  if (!n) return '';
  const d = new Date(n * 1000);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (v) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toUnix = (value, endOfDay = false) => {
  if (!value) return 0;
  const d = new Date(`${value}T${endOfDay ? '23:59:59' : '00:00:00'}`);
  if (Number.isNaN(d.getTime())) return 0;
  return Math.floor(d.getTime() / 1000);
};

const EMPTY = {
  id: 0,
  title: '',
  image: '',
  image_mobile: '',
  alt_text: '',
  link_url: '',
  placement: 'home',
  sort_order: 0,
  starts_at: '',
  ends_at: '',
  active: 1,
};

/** What a reader would see today, said plainly. */
const liveState = (banner) => {
  const now = Math.floor(Date.now() / 1000);
  if (!banner.active) return { label: 'Paused', tone: 'off' };
  if (banner.starts_at && banner.starts_at > now) {
    return { label: `Starts ${toInput(banner.starts_at)}`, tone: 'soon' };
  }
  if (banner.ends_at && banner.ends_at < now) {
    return { label: `Ended ${toInput(banner.ends_at)}`, tone: 'off' };
  }
  return { label: 'Live', tone: 'on' };
};

export default function Banners() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(EMPTY);
  const [saveError, setSaveError] = useState('');
  const [uploading, setUploading] = useState('');
  const imageRef = useRef(null);
  const mobileRef = useRef(null);

  const load = () => {
    axiosPost('banners/list', { 'Content-Type': 'application/json' }, {}, (resp) => {
      if (resp?.success) {
        setItems(resp.data.items || []);
        setSaveError('');
      } else {
        setSaveError(resp?.response || 'Could not load banners.');
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddNew = () => {
    setFormData(EMPTY);
    setSaveError('');
    setIsEditing(true);
  };

  const handleEdit = (banner) => {
    setFormData({
      ...banner,
      starts_at: toInput(banner.starts_at),
      ends_at: toInput(banner.ends_at),
    });
    setSaveError('');
    setIsEditing(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this banner? This cannot be undone.')) return;
    axiosPost('banners/delete', { 'Content-Type': 'application/json' }, { id }, (resp) => {
      if (resp?.success) load();
      else setSaveError(resp?.response || 'Could not delete that banner.');
    });
  };

  const uploadImage = async (file, field) => {
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const resp = await axios.post(`${config.api.url}banners/upload-image`, fd, {
        headers: { api: config.api.key, 'Content-Type': 'multipart/form-data' },
      });
      if (resp?.data?.success) {
        setFormData(prev => ({ ...prev, [field]: resp.data.data.url }));
      } else {
        setSaveError(resp?.data?.response || 'The upload was refused.');
      }
    } catch (err) {
      setSaveError('Could not upload that image.');
    } finally {
      setUploading('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaveError('');

    if (!formData.image) {
      setSaveError('A banner needs an image.');
      return;
    }

    const payload = {
      ...formData,
      sort_order: parseInt(formData.sort_order || 0, 10),
      active: formData.active ? 1 : 0,
      starts_at: toUnix(formData.starts_at),
      // Run to the end of the chosen day, not the start of it
      ends_at: toUnix(formData.ends_at, true),
    };

    axiosPost('banners/update', { 'Content-Type': 'application/json' }, payload, (resp) => {
      if (resp?.success) {
        setIsEditing(false);
        setIsLoading(true);
        load();
      } else {
        setSaveError(resp?.response || 'Saving failed. Nothing was changed.');
      }
    });
  };

  const set = (patch) => setFormData(prev => ({ ...prev, ...patch }));

  return (
    <div className="dashboard-module">
      <div className="DashboardHeader">
        <h1 className="DashboardTitle">Sponsored Banners</h1>
        {!isEditing && (
          <button className="DashboardButton" onClick={handleAddNew}>
            <FaPlus style={{ marginRight: 5 }} /> Add Banner
          </button>
        )}
      </div>

      <p style={{ margin: '0 0 18px', fontSize: 13, lineHeight: '20px', color: '#667085' }}>
        Shown on the home page, in the order below. A banner only appears while it
        is switched on and inside its dates — leave the dates blank to run it until
        you stop it.
      </p>

      {saveError && <p className="DashboardSaveError" role="alert">{saveError}</p>}

      {isLoading ? (
        <p>Loading banners...</p>
      ) : isEditing ? (
        <form onSubmit={handleSubmit} className="DashboardForm">
          <h2 className="DashboardSubtitle">{formData.id ? 'Edit Banner' : 'Add Banner'}</h2>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Banner image</label>
            <p style={{ margin: '0 0 6px', fontSize: 12, color: '#667085' }}>
              A wide image works best — around 1600 × 300.
            </p>
            {formData.image && (
              <img
                src={formData.image}
                alt=""
                style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 6, marginBottom: 8 }}
              />
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                className="DashboardButtonSecondary"
                onClick={() => imageRef.current?.click()}
                disabled={uploading === 'image'}
              >
                {uploading === 'image' ? 'Uploading...' : (formData.image ? 'Replace image' : 'Upload image')}
              </button>
              {formData.image && (
                <button type="button" className="DashboardButtonSecondary" onClick={() => set({ image: '' })}>
                  Remove
                </button>
              )}
            </div>
            <input
              ref={imageRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => { uploadImage(e.target.files[0], 'image'); e.target.value = ''; }}
            />
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Mobile image (optional)</label>
            <p style={{ margin: '0 0 6px', fontSize: 12, color: '#667085' }}>
              A taller crop for phones. Without one the wide image is used.
            </p>
            {formData.image_mobile && (
              <img
                src={formData.image_mobile}
                alt=""
                style={{ width: 200, maxHeight: 160, objectFit: 'cover', borderRadius: 6, marginBottom: 8 }}
              />
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                className="DashboardButtonSecondary"
                onClick={() => mobileRef.current?.click()}
                disabled={uploading === 'image_mobile'}
              >
                {uploading === 'image_mobile' ? 'Uploading...' : (formData.image_mobile ? 'Replace' : 'Upload')}
              </button>
              {formData.image_mobile && (
                <button type="button" className="DashboardButtonSecondary" onClick={() => set({ image_mobile: '' })}>
                  Remove
                </button>
              )}
            </div>
            <input
              ref={mobileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => { uploadImage(e.target.files[0], 'image_mobile'); e.target.value = ''; }}
            />
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Name</label>
            <p style={{ margin: '0 0 4px', fontSize: 12, color: '#667085' }}>
              For your own reference in this list. Readers never see it.
            </p>
            <input
              type="text"
              className="DashboardFormInput"
              value={formData.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="e.g. Hyatt — autumn campaign"
            />
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Links to</label>
            <input
              type="text"
              className="DashboardFormInput"
              value={formData.link_url}
              onChange={(e) => set({ link_url: e.target.value })}
              placeholder="https://..."
            />
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Describe the image</label>
            <p style={{ margin: '0 0 4px', fontSize: 12, color: '#667085' }}>
              Read aloud to people using a screen reader.
            </p>
            <input
              type="text"
              className="DashboardFormInput"
              value={formData.alt_text}
              onChange={(e) => set({ alt_text: e.target.value })}
              placeholder="e.g. Zoetry Curacao Resort and Spa"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Starts</label>
              <input
                type="date"
                className="DashboardFormInput"
                value={formData.starts_at}
                onChange={(e) => set({ starts_at: e.target.value })}
              />
            </div>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Ends</label>
              <input
                type="date"
                className="DashboardFormInput"
                value={formData.ends_at}
                onChange={(e) => set({ ends_at: e.target.value })}
              />
            </div>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Order</label>
              <input
                type="number"
                className="DashboardFormInput"
                value={formData.sort_order}
                onChange={(e) => set({ sort_order: e.target.value })}
              />
            </div>
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={!!formData.active}
                onChange={(e) => set({ active: e.target.checked ? 1 : 0 })}
              />
              Show on the site
            </label>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" className="DashboardButtonSecondary" onClick={() => setIsEditing(false)}>
              Cancel
            </button>
            <button type="submit" className="DashboardButton">
              {formData.id ? 'Update Banner' : 'Add Banner'}
            </button>
          </div>
        </form>
      ) : (
        <table className="DashboardTable">
          <thead>
            <tr>
              <th>Preview</th>
              <th>Name</th>
              <th>Links to</th>
              <th>Status</th>
              <th>Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={6} style={{ color: '#667085' }}>
                  No banners yet. Add one to show a sponsored placement on the home page.
                </td>
              </tr>
            )}
            {items.map((banner) => {
              const state = liveState(banner);
              return (
                <tr key={banner.id}>
                  <td>
                    {banner.image && (
                      <img
                        src={banner.image}
                        alt=""
                        style={{ width: 140, height: 44, objectFit: 'cover', borderRadius: 4 }}
                      />
                    )}
                  </td>
                  <td>{banner.title || '-'}</td>
                  <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {banner.link_url || '-'}
                  </td>
                  <td>
                    <span style={{
                      fontSize: 12,
                      fontWeight: 700,
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: state.tone === 'on' ? '#e7f6ec' : state.tone === 'soon' ? '#fff6e5' : '#f1f3f5',
                      color: state.tone === 'on' ? '#1b7f43' : state.tone === 'soon' ? '#8a5a00' : '#667085',
                    }}>
                      {state.label}
                    </span>
                  </td>
                  <td>{banner.sort_order}</td>
                  <td>
                    <button onClick={() => handleEdit(banner)} className="DashboardButtonSecondary" style={{ marginRight: 8 }}>
                      <FaEdit /> Edit
                    </button>
                    <button onClick={() => handleDelete(banner.id)} className="DashboardButtonSecondary" style={{ color: '#d32f2f' }}>
                      <FaTrash /> Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
